/**
 * Optional browser error reporting.
 *
 * When `VITE_SENTRY_DSN` is set the Sentry SDK is loaded lazily (so it never
 * ships to deployments that don't use it) and initialised with the release
 * baked in at build time. Without a DSN every function is a cheap no-op.
 */
import type * as Sentry from "@sentry/react";

import { ApiError } from "@/api/problem";

type SentryModule = typeof Sentry;

let sentry: SentryModule | null = null;
let loading: Promise<SentryModule | null> | null = null;

/** Build-time release identifier (`mms-frontend@<version>`). */
export const RELEASE = `mms-frontend@${import.meta.env.VITE_APP_VERSION ?? "dev"}`;

/** Whether telemetry is configured for this build. */
export function telemetryEnabled(): boolean {
  return Boolean(import.meta.env.VITE_SENTRY_DSN);
}

/**
 * Initialise error reporting. Resolves once the SDK is ready (or immediately
 * when telemetry is disabled). Safe to call more than once.
 */
export async function initTelemetry(
  load: () => Promise<SentryModule> = () => import("@sentry/react"),
): Promise<boolean> {
  if (!telemetryEnabled()) {
    return false;
  }
  loading ??= load()
    .then((mod) => {
      mod.init({
        dsn: import.meta.env.VITE_SENTRY_DSN,
        release: RELEASE,
        environment: import.meta.env.MODE,
        tracesSampleRate: 0,
      });
      sentry = mod;
      return mod;
    })
    .catch(() => null);
  return (await loading) !== null;
}

/**
 * Report an unexpected error. Client-side API failures (4xx) are expected
 * application states and are deliberately not reported.
 */
export function captureException(error: unknown, context?: Record<string, unknown>): void {
  if (error instanceof ApiError && error.status > 0 && error.status < 500) {
    return;
  }
  sentry?.captureException(error, context ? { extra: context } : undefined);
}

/** Attach the signed-in member to subsequent reports (id only, no PII). */
export function setTelemetryUser(user: { id: string; tenant: string } | null): void {
  if (!sentry) {
    return;
  }
  sentry.setUser(user ? { id: user.id } : null);
  sentry.setTag("tenant", user?.tenant ?? "");
}

/**
 * Manual verification hook: opening any page with `?sentry-test=1` sends one
 * test event once the SDK is loaded, and reports the event id to the console.
 * Only active when telemetry is enabled, so it is inert in normal builds.
 */
export async function maybeSendTestEvent(
  search: string = globalThis.location?.search ?? "",
): Promise<string | null> {
  if (!new URLSearchParams(search).has("sentry-test") || !(await initTelemetry())) {
    return null;
  }
  const id = sentry?.captureMessage("MMS Sentry test event — safe to resolve", "info") ?? null;
  console.info(`[telemetry] sent Sentry test event ${id ?? "?"} (${RELEASE})`);
  return id;
}

/** Reset module state (tests only). */
export function resetTelemetryForTests(): void {
  sentry = null;
  loading = null;
}
